// src/pages/UserPanel.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Navbar from "../components/layout/Navbar";
import WeekCalendar from "../components/appointments/WeekCalendar";
import PatientCard from "../components/patients/PatientCard";
import PatientDetails from "../components/patients/PatientDetails";
import AppointmentsModal from "../components/appointments/AppointmentsModal";
import Loader from "../components/ui/Loader";
import SearchInput from "../components/ui/SearchInput";
import { usePatients } from "../context/PatientsContext";
import { useAppointments } from "../context/AppointmentsContext";
import { useAuth } from "../context/AuthContext";
import DoctorFinancialModal from "../components/patients/DoctorFinancialModal";

export default function UserPanel() {
  const { currentUser } = useAuth();
  const { pacienti = [], loading, refreshPatients } = usePatients();
  const { appointments = [] } = useAppointments();

  const myPatients = useMemo(() => {
    if (!currentUser?.email) return [];
    const email = currentUser.email.trim().toLowerCase();
    return pacienti.filter(
      (patient) => (patient.medicEmail || "").trim().toLowerCase() === email
    );
  }, [pacienti, currentUser]);

  const myAppointments = useMemo(() => {
    if (!currentUser?.email) return [];
    const email = currentUser.email.trim().toLowerCase();
    return appointments.filter((a) =>
      (a.createdBy || "").trim().toLowerCase() === email ||
      (a.doctorEmail || "").trim().toLowerCase() === email
    );
  }, [appointments, currentUser]);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [showAppointmentsModal, setShowAppointmentsModal] = useState(false);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [activePatientId, setActivePatientId] = useState(null);
  const clearActivePatientSelection = useCallback(() => {
    setActivePatientId(null);
    setSelectedPatient(null);
    setShowPatientDetails(false);
    setShowAppointmentsModal(false);
    setSearchValue("");
    setSearchDropdownOpen(false);
  }, []);

  const formatName = useCallback(
    (patient) =>
      `${patient.lastName || ""} ${patient.firstName || ""}`.replace(/\s+/g, " ").trim(),
    []
  );

  const myPatientIds = useMemo(
    () => new Set(myPatients.map((patient) => patient.id)),
    [myPatients]
  );

  const allPatientOptions = useMemo(
    () =>
      pacienti.map((patient) => {
        const fullName = formatName(patient) || "Pacient fără nume";
        return {
          id: patient.id,
          displayName: fullName,
          patient,
          isOwned: myPatientIds.has(patient.id),
        };
      }),
    [pacienti, formatName, myPatientIds]
  );

  const filteredOptions = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return allPatientOptions;
    return allPatientOptions.filter((item) => {
      const haystack = [
        item.displayName,
        item.patient.email,
        item.patient.code,
        item.patient.cnp,
        item.patient.phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [allPatientOptions, searchValue]);

  const activePatient = useMemo(
    () => myPatients.find((patient) => patient.id === activePatientId) || null,
    [myPatients, activePatientId]
  );

  useEffect(() => {
    refreshPatients();
  }, [refreshPatients]);

  useEffect(() => {
    if (!activePatientId) return;
    if (!activePatient) {
      setActivePatientId(null);
      setSelectedPatient(null);
      setSearchValue("");
    }
  }, [activePatientId, activePatient]);

  if (!currentUser) {
    return <p className="text-center mt-10">Autentificare necesară</p>;
  }

  const allowedRoles = ["doctor", "assistant"];
  if (!allowedRoles.includes(currentUser.role)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-600">
        <p className="text-center text-base sm:text-lg">
          Acest panou este disponibil doar medicilor și asistenților.
        </p>
      </div>
    );
  }

  const handleOpenFinancial = useCallback(() => {
    setShowFinancialModal(true);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar
        showFinancialButton
        onOpenFinancial={handleOpenFinancial}
      />

      <main className="flex-grow pt-24 sm:pt-28 p-4 sm:p-6 max-w-6xl mx-auto w-full">
        <section className="bg-white mt-8 sm:mt-12 rounded-2xl shadow p-4 sm:p-6 mb-6">
          <div className="space-y-3">
            <h1 className="text-2xl font-bold text-gray-800">Pacienții mei</h1>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="w-full sm:max-w-[50%] lg:max-w-md">
                <SearchInput
                  value={searchValue}
                  onChange={(value) => {
                    setSearchValue(value);
                    setSearchDropdownOpen(true);
                  }}
                  placeholder="Caută pacient după nume, CNP, email..."
                  results={filteredOptions}
                  renderResult={(item) => (
                    <span
                      className={`font-medium ${
                        item.isOwned ? "text-gray-800" : "text-gray-400"
                      }`}
                    >
                      {item.displayName}
                      {!item.isOwned ? " • alt medic" : ""}
                    </span>
                  )}
                  onSelect={(item) => {
                    if (!item.isOwned) {
                      toast.info("Pacientul nu este asociat contului tău.");
                      return;
                    }
                    setActivePatientId(item.id);
                    setSelectedPatient(null);
                    setSearchValue("");
                    setSearchDropdownOpen(false);
                  }}
                  onClear={() => {
                    clearActivePatientSelection();
                  }}
                  noResultsLabel="Nu există pacienți pentru termenul introdus"
                  showDropdown={searchDropdownOpen}
                  setShowDropdown={setSearchDropdownOpen}
                  inputProps={{ autoComplete: "off" }}
                />
              </div>
            </div>
          </div>
        </section>

        {loading && <Loader text="Se încarcă pacienții..." />}

        {!loading && myPatients.length === 0 && (
          <p className="text-gray-600 text-center mt-10">
            Nu există pacienți asociați contului tău.
          </p>
        )}

        {activePatient && !loading && (
          <div className="grid gap-6">
            <PatientCard
              key={activePatient.id}
              currentUser={currentUser}
              patient={activePatient}
              appointmentsList={myAppointments.filter((a) => a.patientId === activePatient.id)}
              showJournal={currentUser?.role === "admin"}
              onClose={clearActivePatientSelection}
              onDetails={() => {
                setSelectedPatient(activePatient);
                setShowPatientDetails(true);
              }}
              onAppointments={() => {
                setSelectedPatient(activePatient);
                setShowAppointmentsModal(true);
              }}
              onDelete={() => {
                clearActivePatientSelection();
                refreshPatients();
              }}
            />
          </div>
        )}

        {/* calendar */}
        <section className="bg-white rounded-2xl shadow p-2 sm:p-4 mt-8 mb-6 overflow-x-auto">
          <WeekCalendar />
        </section>
      </main>

      {/* Modale */}
      {showPatientDetails && selectedPatient && (
        <PatientDetails
          patient={selectedPatient}
          currentUser={currentUser}
          allowEditing={currentUser?.role === "admin"}
          onClose={() => setShowPatientDetails(false)}
          onSave={(updated, { silent } = {}) => {
            if (updated) {
              setSelectedPatient(updated);
            }
            if (!silent) {
              setShowPatientDetails(false);
              refreshPatients();
            }
          }}
        />
      )}

      {showAppointmentsModal && selectedPatient && (
        <AppointmentsModal
          title={`Programările pacientului – ${selectedPatient.lastName} ${selectedPatient.firstName}`}
          appointments={myAppointments.filter((a) => a.patientId === selectedPatient.id)}
          patientNameFallback={`${selectedPatient.lastName} ${selectedPatient.firstName}`}
          onClose={() => setShowAppointmentsModal(false)}
        />
      )}

      {showFinancialModal && (
        <DoctorFinancialModal onClose={() => setShowFinancialModal(false)} />
      )}

    </div>
  );
}
